import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'

/**
 * Tenant-aware replacement for the static `/images/bucket_image.png`.
 *
 * The PNG baked in SportNinja amber with a dark glyph, so it rendered wrong on
 * Canlan. This builds the same artwork as an SVG data URI using the tenant
 * palette: `colors.PRIMARY` for the rounded square and
 * `PRIMARY_BUTTON_CONTENT.icon` for the roster glyph on top of it.
 *
 * Exposed as a `src` string (not a React component) because every call site
 * feeds it to an `<img src>` / `TeamImage src` / antd `Image src`, often inside
 * a ternary against other image paths and once from a plain-JS `onError`
 * handler.
 */
const buildBucketImageSvg = (): string => {
  const background = colors.PRIMARY
  const glyph = PRIMARY_BUTTON_CONTENT.icon

  // 128x128 to match the original asset's intrinsic size.
  //
  // The head circle needs clear background around it on every side. Rather
  // than mask the whole glyph (which leaves ragged cuts through the document
  // border and text lines), the pieces that would collide are shortened at the
  // source: the document's right edge stops higher, and the top two text lines
  // are trimmed. Only the shoulders are masked, since their arc genuinely
  // wraps the head.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128" fill="none">
  <defs>
    <mask id="snBucketHeadCut">
      <rect width="128" height="128" fill="white"/>
      <circle cx="84" cy="67" r="19.5" fill="black"/>
    </mask>
  </defs>
  <rect width="128" height="128" rx="28" fill="${background}"/>
  <path d="M34 38a6 6 0 0 1 6-6h48a6 6 0 0 1 6 6v14.6a25 25 0 0 0-7-2.6V39H41v50h12.4a25 25 0 0 0 2.3 7H40a6 6 0 0 1-6-6V38Z" fill="${glyph}"/>
  <rect x="48" y="48" width="26" height="7" rx="3.5" fill="${glyph}"/>
  <rect x="48" y="61" width="14" height="7" rx="3.5" fill="${glyph}"/>
  <rect x="48" y="74" width="16" height="7" rx="3.5" fill="${glyph}"/>
  <g mask="url(#snBucketHeadCut)">
    <path d="M66 99c0-4.6 2.4-8.7 6.2-11.3l9.3 8a4 4 0 0 0 5 0l9.3-8c3.8 2.6 6.2 6.7 6.2 11.3v5H66v-5Z" fill="${glyph}"/>
  </g>
  <path d="M84 54a13 13 0 1 0 0 26 13 13 0 0 0 0-26Zm0 8a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" fill="${glyph}"/>
</svg>`

  // encodeURIComponent (not base64) keeps the markup readable in devtools and
  // avoids a Buffer/btoa dependency.
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

/**
 * Tenant-aware bucket/roster image, ready to drop into any `src` prop.
 * Computed once at module load — `colors` is resolved at build time.
 */
export const BUCKET_IMAGE_SRC = buildBucketImageSvg()

export default BUCKET_IMAGE_SRC
